import { useEffect, useState } from 'react'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { Box, Paper, styled } from '@mui/material'
import { useApplicationSettings, useGroup } from 'hooks'
import { Overview } from './Overview'
import { LeftPaneActions, LeftPaneTabs } from 'components'
import { ResultantMembers } from './ResultantMembers'
import { Members } from './Members'
import { MembershipChanges } from './MembershipChanges'
import { useIsSmallScreenFalse, useQuery } from 'packages/core'
import StickyContainer from 'components/StickyContainer'
import { ItemDetails, Button, TextFormatter } from 'components'
import bgHeader from '../../../assets/images/bgHeader.png'
import { useTranslation } from 'react-i18next'
import useSubcomponents from 'useSubcomponents'
import { useHistory } from 'react-router'
import { PageContextProvider } from 'pageContext'
import { useAppState } from 'appContext'
import { NestedGroupMembers } from './NestedGroupMembers'
import { RbacAssignments } from './RbacAssignments'
import {
    getAccessibleTabs,
    RESOURCE_TYPE_NAMES,
    updateUrlQueryParams,
} from 'utils'
import { AccessRequestPolicy } from 'components/AccessRequestPolicy/AccessRequestPolicy'
import { DirectAssignedLocations } from './DirectAssignedLocations'
import { AccessManagers } from './AccessManagers(Owners)'

const HeaderContainer = styled(Box)({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '0px 48px 0px 32px',
    overflow: 'hidden',
    color: '#ffffff',
    fontSize: '22px',
    textAlign: 'center',
})
const smallScreenWidth = 960

const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            position: 'relative',
            height: '100%',
            width: '100%',

            [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                flexDirection: 'column',
            },
        },
        imgContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            width: '100%',
            minHeight: '125px',
            borderRadius: '8px',
            marginBottom: '20px',

            '& img': {
                maxHeight: '80px',
                maxWidth: '80px',
            },

            [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                minWidth: '116px',
                '& img': {
                    maxHeight: '33px',
                    width: '66px',
                },
            },
        },
        nameContainer: {
            width: '100%',
            minHeight: '50px',
            marginBottom: '20px',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            overflow: 'hidden',
            fontSize: '22px',
            fontWeight: 'bold',
            borderRadius: '8px',
            color: '#3b454d',
            wordBreak: 'break-word',
            padding: '5px',
        },

        tabsContainer: {
            width: '100%',
            borderRadius: '0px',
            marginBottom: '20px',
        },
        section: {
            display: 'flex',
            justifyContent: 'flex-start',
            marginLeft: theme.spacing(2),
            marginRight: theme.spacing(2),
            flexWrap: 'wrap',
            width: '100%',
            [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                display: 'none',
            },
        },

        basicInfo: {
            display: 'flex',
            flexDirection: 'column',
        },
        description: {
            [`@media (min-width:1366px)`]: {
                maxWidth: '300px',
            },
        },
        actionsContainer: {
            minWidth: '230px',
            paddingRight: theme.spacing(2),

            [`@media (max-width:445px)`]: {
                alignItems: 'flex-start',
            },
        },
        backButton: {
            '& >button': {
                '&:hover': {
                    backgroundColor: theme?.palette?.primary?.main,
                    color: 'white',
                    '& span': {
                        color: theme.palette.common.white,
                    },
                },
            },
        },
        moreInfoButton: {
            marginTop: theme.spacing(3),
            '& >button': {
                backgroundColor: theme.palette.grey[900],
                color: theme.palette.common.white,

                border: `solid 1px ${theme.palette.grey[900]}`,

                '&:hover': {
                    backgroundColor: theme.palette.grey[900],

                    '& span': {
                        //  color: theme.palette.common.white,
                    },
                },
            },
        },
        actionDropdown: {
            marginTop: '40px',
        },
        showMoreButton: {
            [`@media (min-width:${smallScreenWidth}px)`]: {
                display: 'none',
            },

            position: 'absolute',
            bottom: '-15px',
            left: 'calc(50% - 83px)',
            zIndex: 2,
            '& > button': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#ffffff',
                border: 'none',
                height: '32px',
                minWidth: '115px',
                color: theme?.palette?.primary?.main,
                fontSize: '11px',
                textTransform: 'uppercase',
                borderRadius: '15px',
                boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
                cursor: 'pointer',
                paddingLeft: '12px',
                paddingRight: '6px',
                '& svg': {
                    color: theme?.palette?.primary?.main,
                    height: '20px',
                },
            },
        },
        collapsibleContainer: {
            display: 'flex',
            flexDirection: 'column',
            [`@media (min-width:${smallScreenWidth}px)`]: {
                display: 'none',
            },
            '& div': {
                textAlign: 'start',
            },
        },
        actionContainerMobile: {},
        azureApplicationDetailsLabel: {
            color: '#1b1f1f',
            textDecoration: 'underline',
        },

        azureApplicationDetailsIdValues: {
            color: theme?.palette?.primary?.main,
        },

        content: {
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',

            [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                paddingBottom: theme.spacing(2),
            },
            [`@media (max-width:345px)`]: {
                flexDirection: 'column-reverse',
            },
        },
        commonDeputies: {
            'flex-wrap': 'nowrap',
        },
    }),
)

export const TabsColumn = styled(Box)({
    display: 'flex',
    minWidth: '340px',
    flexDirection: 'column',
    margin: '0px 12px 0px 10px',
    marginRight: '20px',
    width: '30%',
    flex: '36%',
})

export const GroupDetails = (props) => {
    const classes = useStyles()
    const [, dispatch] = useAppState()
    const { tabs, basicAttributes, groupId } = props
    const { data: group, refetch } = useGroup(groupId)
    const query = useQuery()
    const isSmallScreen = useIsSmallScreenFalse()
    const { data: appConfig } = useApplicationSettings()
    const { t } = useTranslation()

    const { hasAccessToControl } = useSubcomponents()
    const history = useHistory()

    const [activeTab, setActiveTab] = useState(null)
    const [allAccessibleTabs, setAllAccessibleTabs] = useState([])

    useEffect(() => {
        if (group) {
            const queryActiveTab = query.get('activeTab')
            const queryActiveSubTab = query.get('subActiveTab')
            const activeTabInfo = getAccessibleTabs(
                query,
                history,
                tabs,
                queryActiveTab,
                queryActiveSubTab,
                hasAccessToControl,
                'activeTab',
                'subActiveTab',
            )
            setAllAccessibleTabs(activeTabInfo.allAccessibleTabs)
            setActiveTab(activeTabInfo.activeTab)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [group])

    const handleTabChange = (tab) => {
        setActiveTab(tab)
        updateUrlQueryParams(
            query,
            history,
            [{ key: 'activeTab', value: tab }],
            ['subActiveTab', 'returnedFromWorkflow'],
        )
    }

    useEffect(() => {
        if (groupId && group?.id) {
            dispatch({
                type: 'SET_CURRENT_RESOURCE',
                payload: {
                    targetAssigneeId: groupId,
                    targetAssigneeFriendlyName:
                        group?.friendlyName ?? t(RESOURCE_TYPE_NAMES.GROUPS),
                    targetAssignee_ResourceTypeFriendlyName: t(
                        RESOURCE_TYPE_NAMES.GROUPS,
                    ),
                },
            })

            dispatch({
                type: 'SET_TARGET_RESOURCE',
                payload: {
                    targetAssigneeId: groupId,
                    targetAssigneeFriendlyName:
                        group?.friendlyName ?? t(RESOURCE_TYPE_NAMES.GROUPS),
                    targetAssignee_ResourceTypeFriendlyName: t(
                        RESOURCE_TYPE_NAMES.GROUPS,
                    ),
                },
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId, group?.id])

    if (isSmallScreen) {
        return (
            <>
                <StickyContainer
                    color="#ffffff"
                    styles={{
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    }}
                >
                    <Box
                        width="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="column"
                    >
                        <Box paddingY="10px" width="100%" maxWidth="260px">
                            <Button.BackButton
                                onClick={() => {
                                    history.push(history.location.pathname)
                                }}
                            >
                                {t('BackToGroupsList')}
                            </Button.BackButton>
                        </Box>

                        <HeaderContainer
                            height="80px"
                            textAlign="start"
                            bgcolor={appConfig.style.colors.primary?.header}
                            style={{
                                backgroundImage: `url(${bgHeader})`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center',
                            }}
                        >
                            <TextFormatter
                                value={group?.name ?? group?.description}
                                maxCharacters={75}
                            />
                        </HeaderContainer>
                    </Box>
                </StickyContainer>
                <ItemDetails.Tabs
                    variant="scrollable"
                    value={activeTab}
                    onChange={(_, value) => handleTabChange(value)}
                    style={{ background: '#fbfbfd' }}
                >
                    {allAccessibleTabs.map((tab, index) => (
                        <ItemDetails.Tabs.Tab
                            key={index + tab.value}
                            value={tab.value}
                            label={t(tab.label)}
                        />
                    ))}
                </ItemDetails.Tabs>
                <Box className={classes.root}>
                    {activeTab === 'overview' && (
                        <PageContextProvider key={'GroupsOverview'}>
                            <Overview
                                groupId={groupId}
                                basicAttributes={basicAttributes}
                            />
                        </PageContextProvider>
                    )}
                    {activeTab === 'resultantmembers' && (
                        <PageContextProvider key={'GroupsResultantMembers'}>
                            <ResultantMembers groupId={groupId} />
                        </PageContextProvider>
                    )}
                    {activeTab === 'members' && (
                        <PageContextProvider key={'GroupsMembers'}>
                            <Members groupId={groupId} />
                        </PageContextProvider>
                    )}
                    {activeTab === 'membershipchanges' && (
                        <PageContextProvider key={'GroupsMembershipChanges'}>
                            <MembershipChanges groupId={groupId} />
                        </PageContextProvider>
                    )}
                    {activeTab === 'nestedgroupmembers' && (
                        <PageContextProvider key={'NestedGroupMembers'}>
                            <NestedGroupMembers groupId={groupId} />
                        </PageContextProvider>
                    )}
                    {activeTab === 'rbacassignments' && (
                        <PageContextProvider
                            key={'RbacAssignmentsGroupsDetails'}
                        >
                            <RbacAssignments groupId={groupId} group={group} />
                        </PageContextProvider>
                    )}
                    {activeTab === 'accessManagers' && (
                        <PageContextProvider key="RequestViewGroupsAccessManagers">
                            <AccessManagers resource={group} />
                        </PageContextProvider>
                    )}
                    {activeTab === 'directAssignedLocations' && (
                        <PageContextProvider key="DirectAssignedLocations">
                            <DirectAssignedLocations id={groupId} />
                        </PageContextProvider>
                    )}

                    {activeTab === 'accessRequestPolicy' && (
                        <PageContextProvider key="AccessRequestPolicy">
                            <AccessRequestPolicy
                                resourceId={groupId}
                                requestPolicyId={group?.requestPolicyId}
                                refetch={refetch}
                            />
                        </PageContextProvider>
                    )}
                </Box>
            </>
        )
    }
    return (
        <>
            <Box display="flex">
                <TabsColumn>
                    <Paper className={classes.nameContainer}>
                        {group?.friendlyName}
                    </Paper>
                    <Paper className={classes.tabsContainer}>
                        <LeftPaneTabs
                            active={activeTab}
                            onChange={handleTabChange}
                            application={group}
                            tabs={allAccessibleTabs}
                        />
                    </Paper>
                    <LeftPaneActions
                        resource={group}
                        resourceType="groups"
                        activeTab={activeTab}
                        verb={'Group'}
                    />
                </TabsColumn>
                <Box className={classes.root}>
                    {activeTab === 'overview' && (
                        <PageContextProvider key={'GroupsOverview'}>
                            <Paper>
                                <Overview
                                    groupId={groupId}
                                    basicAttributes={basicAttributes}
                                />
                            </Paper>
                        </PageContextProvider>
                    )}
                    {activeTab === 'resultantmembers' && (
                        <PageContextProvider key={'GroupsResultantMembers'}>
                            <ResultantMembers groupId={groupId} />
                        </PageContextProvider>
                    )}
                    {activeTab === 'members' && (
                        <PageContextProvider key={'GroupsMembers'}>
                            <Members groupId={groupId} />
                        </PageContextProvider>
                    )}
                    {activeTab === 'membershipchanges' && (
                        <PageContextProvider key={'GroupsMembershipChanges'}>
                            <MembershipChanges groupId={groupId} />
                        </PageContextProvider>
                    )}
                    {activeTab === 'nestedgroupmembers' && (
                        <PageContextProvider key={'NestedGroupMembers'}>
                            <NestedGroupMembers group={group} />
                        </PageContextProvider>
                    )}
                    {activeTab === 'rbacassignments' && (
                        <PageContextProvider
                            key={'RbacAssignmentsGroupsDetails'}
                        >
                            <RbacAssignments groupId={groupId} group={group} />
                        </PageContextProvider>
                    )}
                    {activeTab === 'accessManagers' && (
                        <PageContextProvider key="RequestViewGroupsAccessManagers">
                            <AccessManagers resource={group} />
                        </PageContextProvider>
                    )}
                    {activeTab === 'directAssignedLocations' && (
                        <PageContextProvider key="DirectAssignedLocations">
                            <DirectAssignedLocations id={groupId} />
                        </PageContextProvider>
                    )}
                    {activeTab === 'accessRequestPolicy' && (
                        <PageContextProvider key="AccessRequestPolicy">
                            <AccessRequestPolicy
                                resourceId={groupId}
                                requestPolicyId={group?.requestPolicyId}
                                refetch={refetch}
                            />
                        </PageContextProvider>
                    )}
                </Box>
            </Box>
        </>
    )
}
